import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useRecordingDetail } from '../hooks/useRecordingDetail';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { fetchAndStoreApiResults } from '../redux/slices/recordingsSlice';

// Add shimmer animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  .animate-shimmer {
    animation: shimmer 1.5s ease-in-out infinite;
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = shimmerStyles;
  document.head.appendChild(style);
}

// Additional icons available for future use:
// FiPlay, FiPause, FiDownload, FiShare2, FiMoreVertical,
// FiClock, FiUser, FiCalendar, FiTarget, FiTrendingUp,
// FiAlertCircle, FiCheckCircle, FiCopy, FiEdit3, FiTrash2,
// FiSettings, FiBookmark, FiTag, FiPieChart, FiSearch

// Constants for free trial limitations
const FREE_TRIAL_ORG_ID = 'org_33nodgVx3c02DhIoiT1Wen7Xgup';
const FREE_TRIAL_ROLE = 'org:free_trial';
const FREE_TRIAL_TIME_LIMIT_SECONDS = 300; // 5 minutes

// Skeleton loading components with shimmer animation
const SkeletonBar: React.FC<{ width?: string; height?: string; className?: string }> = ({ 
  width = "100%", 
  height = "16px", 
  className = "" 
}) => (
  <div 
    className={`bg-gray-200 rounded animate-pulse relative overflow-hidden ${className}`}
    style={{ width, height }}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-60 animate-shimmer"></div>
  </div>
);

const TextSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: lines }).map((_, index) => (
      <SkeletonBar 
        key={index}
        width={index === lines - 1 ? "75%" : "100%"} 
        height="16px"
      />
    ))}
  </div>
);

const BadgesSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="flex flex-wrap gap-2">
    {Array.from({ length: count }).map((_, index) => (
      <SkeletonBar 
        key={index}
        width="95px" 
        height="24px"
        className="rounded-full"
      />
    ))}
  </div>
);

const ListItemSkeleton: React.FC = () => (
  <div className="flex gap-4 p-3 rounded-lg bg-gray-50">
    <SkeletonBar width="45px" height="20px" />
    <div className="flex-1 space-y-2">
      <SkeletonBar width="100%" height="16px" />
      <SkeletonBar width="80%" height="16px" />
    </div>
  </div>
);

const IntelligenceItemSkeleton: React.FC = () => (
  <div className="border rounded-lg p-4 space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex-1 space-y-2">
        <SkeletonBar width="100%" height="16px" />
        <SkeletonBar width="85%" height="16px" />
      </div>
      <SkeletonBar width="60px" height="20px" className="ml-2 rounded-full" />
    </div>
    <div className="flex items-center gap-2">
      <SkeletonBar width="40px" height="14px" />
      <SkeletonBar width="80px" height="14px" />
    </div>
  </div>
);

const RecordingDetail: React.FC = () => {
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();

  // Check if user is on free trial by examining organization memberships
  const isFreeTrial = useMemo(() => {
    if (!clerkUser?.organizationMemberships) return false;
    
    return clerkUser.organizationMemberships.some(membership => 
      membership.organization.id === FREE_TRIAL_ORG_ID && 
      membership.role === FREE_TRIAL_ROLE
    );
  }, [clerkUser]);

  const handleSubscribe = () => {
    console.log('Navigate to subscription page');
    navigate('/app/billing');
  };

  // API Integration: Fetch server-side results when available
  const { getToken } = useAuth();

  // Redux integration: Get recording data from store instead of direct API calls
  const dispatch = useAppDispatch();

  // Use recording utilities hook with all state management
  const {
    // State variables
    audioUrl,
    audioFileName,
    setIsLoadingAudio,
    detailedIntelligence,
    transcriptView,
    setTranscriptView,
    searchQuery,
    setSearchQuery,
    fullTranscriptSearchQuery,
    setFullTranscriptSearchQuery,
    selectedTimeRange,
    setSelectedTimeRange,
    actionItemsSearch,
    setActionItemsSearch,
    decisionsSearch,
    setDecisionsSearch,
    issuesSearch,
    setIssuesSearch,
    questionsSearch,
    setQuestionsSearch,
    activeAutomationTab,
    setActiveAutomationTab,
    isOtherExportsOpen,
    setIsOtherExportsOpen,
    currentAudioTime,
    isAudioPlaying,
    audioRef,

    // Data management state from hook
    dataAPI,
    reduxRecording,
    activeTranscriptData,
    dataSource,
    isFetchingResults,
    resultsError,

    // Audio visualization state
    audioContext,
    analyserNode,
    frequencyData,
    setupAudioAnalysis,
    updateFrequencyData,

    // Plain constants
    fullSegmentButtons,
    fullOnlyButtons,
    segmentsOnlyButtons,
    completeAnalysisButtons,
    actionItemsButtons,
    decisionsButtons,
    issuesButtons,
    questionsButtons,

    // Computed values
    timeRangeOptions,
    filteredSegments,
    currentActiveSegment,
    filteredActionItems,
    filteredDecisions,
    filteredIssues,
    filteredQuestions,
    currentActiveItems,
    hasIntelligenceData,

    // Free trial computed values
    hasExceededFreeTrialLimit,
    freeTrialTimeRangeOptions,

    // Export functions
    getSeverityVariant,
    formatDate,
    formatTimestampFromString,  
    formatSegmentTime,
    renderHighlightedText,
    exportTranscriptData,
    exportIntelligenceData,
    
    // Email export
    emailTranscriptData,
    emailIntelligenceData,
    isEmailingTranscript,
    emailSuccess,
    emailError
  } = useRecordingDetail({ isFreeTrial, freeTrialTimeLimitSeconds: FREE_TRIAL_TIME_LIMIT_SECONDS }, {
    dispatch,
    getToken
  });

  // Prevent lint warnings for hook-provided variables
  void setIsLoadingAudio; void audioContext; void analyserNode; 
  void setupAudioAnalysis; void updateFrequencyData;

  if (!activeTranscriptData) {
    if (dataSource === 'loading' || isFetchingResults) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md text-center bg-transparent border-none border-0">
            <CardHeader>
              <CardTitle>Loading Recording...</CardTitle>
              <CardDescription>Please wait while we load the recording data.</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Recording Not Found</CardTitle>
            <CardDescription>The requested recording could not be found.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/app/recordings">
              <Button variant="outline" className='text-xs font-light'>← Back to Recordings</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] space-y-6">
      <div className="space-y-4">
        <Link to="/app/recordings">
          <Button variant="outline" className='text-xs font-light'>← Back to Recordings</Button>
        </Link>
        
        {/* API Integration Status */}
        {isFetchingResults && (
          <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded-lg border border-blue-200">
            🔄 Fetching latest processing results from server...
          </div>
        )}
        {resultsError && (
          <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded-lg border border-amber-200">
            ⚠️ {resultsError}
          </div>
        )}
        {!resultsError && !isFetchingResults && (dataSource === 'api' || dataSource === 'redux') && !hasIntelligenceData && (
          <div className="text-sm text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200">
            🧠 Intelligence processing... results will appear when ready.
          </div>
        )}
        {/*
        Debug banners are disabled for end users, but kept here for future troubleshooting.
        {dataSource === 'redux' && (
          <div className="text-sm text-green-600 bg-green-50 p-2 rounded-lg border border-green-200">
            ✅ Using live data from Redux store
          </div>
        )}
        {dataSource === 'api' && (
          <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded-lg border border-blue-200">
            ✅ Using live data from API (stored in Redux)
          </div>
        )}
        {dataSource === 'sample' && (
          <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-200">
            📊 Using sample data for demonstration (live data not available)
          </div>
        )}
        */}
        
        <div>
          <div className="flex items-center gap-3 mb-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {
                dataSource === 'api' ? 
                  audioFileName?.split('.')[0] || dataAPI?.recording?.file_name?.split('.')[0] || 'Unknown File' :
                  <SkeletonBar width="200px" height="24px" />
              }
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mt-1">Recording Details and Analysis</p>
        </div>
      </div>

      <Tabs defaultValue="automations" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Overview
          </TabsTrigger>
          <TabsTrigger value="automations" className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            Automations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Audio Player Section */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Recording Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="mb-2 text-sm font-medium text-gray-500 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 3v10a2 2 0 002 2h6a2 2 0 002-2V7H7zM9 7h6M9 11h6m-3 4h.01" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12a9 9 0 1018 0 9 9 0 00-18 0z" />
                    </svg>
                    Audio Playback
                  </label>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-2 rounded-lg border border-blue-100">
                    <div className="mb-4">
                      {/* <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {sampleTranscriptData.file_name}
                      </h4> */}
                      <audio 
                        controls 
                        className="w-full h-12"
                        preload="metadata"
                        style={{
                          filter: 'sepia(20%) saturate(70%) hue-rotate(200deg) brightness(1.1)',
                        }}
                      >
                        {audioUrl && <source src={audioUrl} type="audio/mpeg" />}
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                          MP3 Audio
                        </Badge>
                        {/* <span className="text-gray-600">
                          Duration: {Math.floor(sampleTranscriptData.duration_seconds / 60)}:{String(Math.floor(sampleTranscriptData.duration_seconds % 60)).padStart(2, '0')}
                        </span> */}
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <span className="text-xs">🎧 High Quality</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  {
                    dataSource === 'api' ? (
                      <>
                        <label className="text-sm font-medium text-gray-500">Name</label>
                        <p className="text-gray-900">
                          {audioFileName?.split('.')[0] || dataAPI?.recording?.file_name?.split('.')[0] || activeTranscriptData.recording?.file_name?.split('.')[0] || 'Unknown File'}
                        </p>
                      </>
                    ) : (
                      <>
                        <SkeletonBar width="100px" height="12px" className='mb-4' />
                        <SkeletonBar width="200px" height="18px" />
                      </>
                    )
                  }
                </div>
                <div>
                  {
                    dataSource === 'api' ? (
                      <>
                        <label className="text-sm font-medium text-gray-500">Date Uploaded</label>
                        <p className="text-gray-900">{dataAPI?.intelligence?.processed_at ? formatDate(new Date(dataAPI?.intelligence?.processed_at)) : '-'}</p>
                      </>
                    ) : (
                      <>
                        <SkeletonBar width="100px" height="12px" className='mb-4' />
                        <SkeletonBar width="200px" height="18px" />
                      </>
                    )
                  }
                </div>
                <div>
                  {
                    dataSource === 'api' ? (
                      <>
                        <label className="text-sm font-medium text-gray-500">Duration</label>
                        <Badge variant="secondary">{Math.floor((dataAPI?.recording?.duration_seconds || 0) / 60)}m {Math.floor((dataAPI?.recording?.duration_seconds || 0) % 60)}s</Badge>
                      </>
                    ) : (
                      <>
                        <SkeletonBar width="100px" height="12px" className='mb-4' />
                        <SkeletonBar width="200px" height="18px" />
                      </>
                    )
                  }
                </div>
                
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Executive Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {
                  dataSource === 'api' ? (
                    <>
                      <p className="text-gray-700 mb-4">
                        {dataAPI?.intelligence?.summary || 'No summary available.'}
                      </p>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Content Type: </span>
                          <Badge variant="outline">{dataAPI?.intelligence?.content_type}</Badge>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Segments Processed: </span>
                          <span className="text-gray-900">{dataAPI?.transcript?.segments?.length || 0}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Processed At: </span>
                          <span className="text-gray-900">
                            {new Date(dataAPI?.intelligence?.processed_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-4">
                        <TextSkeleton lines={4} />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <SkeletonBar width="100px" height="14px" />
                          <SkeletonBar width="80px" height="20px" className="rounded-full" />
                        </div>
                        <div className="flex items-center gap-2">
                          <SkeletonBar width="130px" height="14px" />
                          <SkeletonBar width="30px" height="16px" />
                        </div>
                        <div className="flex items-center gap-2">
                          <SkeletonBar width="90px" height="14px" />
                          <SkeletonBar width="150px" height="16px" />
                        </div>
                      </div>
                    </>
                  )
                }
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Key Topics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Topics</CardTitle>
              </CardHeader>
              <CardContent>
                {
                  dataSource === 'api' ? (
                    <div className="flex flex-wrap gap-2">
                      {(dataAPI?.intelligence?.topics || []).map((topic: string, index: number) => (
                        <Badge key={index} variant="secondary">{topic}</Badge>
                      ))}
                    </div>
                  ) : (
                    <BadgesSkeleton count={5} />
                  )
                }
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Analysis Stats</CardTitle>
              </CardHeader>
              <CardContent className={`space-y-4 ${dataSource === 'api' && '!space-y-2'}`}>
                <div className="flex justify-between">
                  {
                    dataSource === 'api' ? (
                      <>
                        <span className="text-sm text-gray-600">Action Items:</span>
                        <Badge variant="outline">{dataAPI?.intelligence?.actions?.length || 0}</Badge>
                      </>
                    ) : (
                      <>
                        <SkeletonBar width="150px" height="12px" />
                        <SkeletonBar width="30px" height="12px" />
                      </>
                    )
                  }
                </div>
                <div className="flex justify-between">
                  {
                    dataSource === 'api' ? (
                      <>
                        <span className="text-sm text-gray-600">Decisions:</span>
                        <Badge variant="outline">{dataAPI?.intelligence?.decisions?.length || 0}</Badge>
                      </>
                    ) : (
                      <>
                        <SkeletonBar width="150px" height="12px" />
                        <SkeletonBar width="30px" height="12px" />
                      </>
                    )
                  }
                </div>
                <div className="flex justify-between">
                  {
                    dataSource === 'api' ? (
                      <>
                        <span className="text-sm text-gray-600">Issues:</span>
                        <Badge variant="outline">{dataAPI?.intelligence?.issues?.length || 0}</Badge>
                      </>
                    ) : (
                      <>
                        <SkeletonBar width="150px" height="12px" />
                        <SkeletonBar width="30px" height="12px" />
                      </>
                    )
                  }
                </div>
                <div className="flex justify-between">
                  {
                    dataSource === 'api' ? (
                      <>
                        <span className="text-sm text-gray-600">Questions:</span>
                        <Badge variant="outline">{dataAPI?.intelligence?.questions?.length || 0}</Badge>
                      </>
                    ) : (
                      <>
                        <SkeletonBar width="150px" height="12px" />
                        <SkeletonBar width="30px" height="12px" />
                      </>
                    )
                  }
                </div>
              </CardContent>
            </Card>

            {/* Available Exports */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Available Exports</CardTitle>
              </CardHeader>
              <CardContent>
                {/* <div className="flex flex-wrap gap-2">
                  {recording.exports?.map((format, index) => (
                    <Badge key={index} variant="outline">{format}</Badge>
                  )) || <span className="text-gray-500">No exports available</span>}
                </div> */}
              </CardContent>
            </Card>
          </div>

          {detailedIntelligence?.confidence_note && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Processing Note</CardTitle>
              </CardHeader>
              <CardContent>
                { 
                  dataSource === 'api' ? (
                    <p className="text-amber-700 bg-amber-50 p-3 text-xs rounded-lg">
                      {dataAPI?.intelligence?.notes || '-'}
                    </p>
                  ) : (
                    <TextSkeleton lines={2} />
                  )
                }
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="automations" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Tabs defaultValue="transcript" onValueChange={setActiveAutomationTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="transcript">Transcript</TabsTrigger>
                    <TabsTrigger value="intelligence">Intelligence</TabsTrigger>
                  </TabsList>

                  <TabsContent value="transcript" className="space-y-6 mt-6">
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg">Transcript</CardTitle>
                        </div>
                        <div>
                          {
                            dataSource === 'api' ? (
                              <CardDescription>
                                Duration: {Math.floor((dataAPI?.transcript?.duration_seconds || 0) / 60)}m {Math.floor((dataAPI?.transcript?.duration_seconds || 0) % 60)}s
                                {dataAPI?.transcript?.segments && ` • ${dataAPI?.transcript?.segments.length} segments`}
                              </CardDescription>
                            ) : (
                              <span className='flex items-center space-x-2'>
                                <SkeletonBar width="120px" height="12px" /> <span>•</span> <SkeletonBar width="90px" height="12px" />
                              </span>
                            )
                          }
                        </div>
                      </CardHeader>
                      <CardContent>
                        {/* Transcript View Toggle */}
                        <div className="mb-4">
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant={transcriptView === 'full' ? 'default' : 'outline'}
                              onClick={() => setTranscriptView('full')}
                            >
                              Full Text
                            </Button>
                            <Button 
                              size="sm" 
                              variant={transcriptView === 'segments' ? 'default' : 'outline'}
                              onClick={() => setTranscriptView('segments')}
                            >
                              Segments
                            </Button>
                          </div>
                        </div>

                        {/* Search and Filter Controls (only show for segments view) */}
                        {transcriptView === 'segments' && (
                          <div className="mb-4 space-y-3">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Search transcript..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  className="w-full"
                                  disabled={dataSource !== 'api'}
                                />
                              </div>
                              <div className="min-w-[150px]">
                                <select
                                  value={selectedTimeRange}
                                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                                  className="w-full p-2 border text-sm border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={currentAudioTime > 0 || isFreeTrial || dataSource !== 'api'}
                                >
                                  {(isFreeTrial ? freeTrialTimeRangeOptions : timeRangeOptions).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {isFreeTrial && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    Free trial: 0 - 5 mins
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Audio-based filtering indicator */}
                            {currentAudioTime > 0 && (
                              <div className="flex items-center gap-2 text-sm bg-blue-50 p-2 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2">
                                  <Badge variant="default" className="bg-blue-100 text-blue-800">
                                    {isAudioPlaying ? '' : 'PAUSED |'} Audio Filter: {Math.floor(Math.floor(currentAudioTime / 300) * 300 / 60)}:00 - {Math.floor((Math.floor(currentAudioTime / 300) * 300 + 300) / 60)}:00
                                  </Badge>
                                  <span className="text-blue-600">({filteredSegments?.length || 0} segments)</span>
                                </div>
                              </div>
                            )}
                            
                            {(searchQuery || (selectedTimeRange !== 'all' && currentAudioTime === 0)) && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>Showing {filteredSegments?.length || 0} of {dataAPI.transcript?.segments?.length || 0} segments</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSearchQuery('');
                                    setSelectedTimeRange('0-10');
                                  }}
                                >
                                  Clear filters
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Search Control for Full Transcript View */}
                        {transcriptView === 'full' && (
                          <div className="mb-4 space-y-3">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Search full transcript..."
                                  value={fullTranscriptSearchQuery}
                                  onChange={(e) => setFullTranscriptSearchQuery(e.target.value)}
                                  className="w-full"
                                  disabled={dataSource !== 'api'}
                                />
                              </div>
                            </div>
                            {fullTranscriptSearchQuery && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>Searching for: "{fullTranscriptSearchQuery}"</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setFullTranscriptSearchQuery('')}
                                >
                                  Clear search
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {transcriptView === 'full' ? (
                          <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                            {dataSource === 'api' ? (
                              <p className="text-xs text-gray-700 whitespace-pre-wrap">
                                {dataAPI.transcript?.full_transcription ? (
                                  renderHighlightedText(dataAPI.transcript.full_transcription, fullTranscriptSearchQuery)
                                ) : (
                                  'Transcript not available yet. Processing may still be in progress.'
                                )}
                              </p>
                            ) : (
                              <TextSkeleton lines={12} />
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {
                              dataSource === 'api' ? (
                                (
                                  (filteredSegments?.length || 0) > 0 ? filteredSegments.map((segment, index) => {
                                    const segmentStart = segment.start_sec ?? segment.start ?? 0;
                                    const segmentEnd = segment.end_sec ?? segment.end ?? 0;
                                    const activeStart = currentActiveSegment?.start_sec ?? currentActiveSegment?.start ?? 0;
                                    const activeEnd = currentActiveSegment?.end_sec ?? currentActiveSegment?.end ?? 0;
                                    const isCurrentlyActive = activeStart === segmentStart && activeEnd === segmentEnd;
                                    return (
                                      <div 
                                        key={index} 
                                        className={`flex gap-4 p-3 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer ${
                                          isCurrentlyActive 
                                            ? 'bg-blue-50 border-l-4 border-l-blue-500 shadow-md' 
                                            : 'bg-gray-50'
                                        }`}
                                      >
                                        <div className="flex-shrink-0 text-left min-w-[45px]">
                                          <div className={`text-xs font-mono mb-1 ${
                                            isCurrentlyActive ? 'text-blue-600 font-bold' : 'text-gray-500'
                                          }`}>
                                            {/* {formatSegmentTime(segment.start)} - {formatSegmentTime(segment.end)} */}
                                            {formatSegmentTime(segment.start_sec ?? segment.start ?? 0)}
                                          </div>
                                          {/* {isCurrentlyActive && (
                                            <Badge variant="default" className="text-xs bg-blue-100 text-blue-800">
                                              Active
                                            </Badge>
                                          )} */}
                                        </div>
                                        <div className="flex-1">
                                          <p className={`text-sm ${
                                            isCurrentlyActive ? 'text-blue-900 font-medium' : 'text-gray-900'
                                          }`}>
                                            {searchQuery ? (
                                              segment.text.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) =>
                                                part.toLowerCase() === searchQuery.toLowerCase() ? (
                                                  <mark key={i} className="bg-yellow-200 px-1 rounded">
                                                    {part}
                                                  </mark>
                                                ) : (
                                                  part
                                                )
                                              )
                                            ) : (
                                              segment.text
                                            )}
                                          </p>
                                          <div className="flex items-center gap-2 mt-2">
                                            <span className={`text-xs ${
                                              isCurrentlyActive ? 'text-blue-600' : 'text-gray-500'
                                            }`}>
                                              Duration: {Math.round((segmentEnd - segmentStart) * 10) / 10}s
                                            </span>
                                            {/* {isCurrentlyActive && (
                                              <span className="text-xs text-blue-600">
                                                Currently playing
                                              </span>
                                            )} */}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }) : (
                                    <div className="bg-gray-50 p-4 rounded-lg text-center text-xs text-gray-500">
                                      {searchQuery || selectedTimeRange !== 'all' 
                                        ? 'No segments match your search criteria' 
                                        : 'No segments available'
                                      }
                                    </div>
                                  )
                                )
                              ) : (
                                // Show skeleton loading for sample data
                                Array.from({ length: 8 }).map((_, index) => (
                                    <ListItemSkeleton key={index} />
                                  )
                                )
                              )
                            }
                          </div>
                        )}

                        {/* Free Trial Upgrade Banner for Segments */}
                        {isFreeTrial && hasExceededFreeTrialLimit && (
                          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="pr-4">
                                <h4 className="text-xs font-medium text-blue-800">More Content Available</h4>
                                <p className="text-xs text-blue-700">
                                  This recording has content beyond 5 minutes. Upgrade to view the complete transcript.
                                </p>
                              </div>
                              <Button
                                onClick={handleSubscribe}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                size="sm"
                              >
                                Subscribe to a plan
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="intelligence" className="space-y-6 mt-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Intelligence Analysis</CardTitle>
                        <CardDescription>
                          Search and filter from your recording
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Tabs defaultValue="action-items" className="w-full">
                          <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="action-items">Action Items</TabsTrigger>
                            <TabsTrigger value="decisions">Decisions</TabsTrigger>
                            <TabsTrigger value="issues">Issues</TabsTrigger>
                            <TabsTrigger value="questions">Questions</TabsTrigger>
                          </TabsList>

                          {/* Action Items Tab */}
                          <TabsContent value="action-items" className="space-y-4">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Search action items..."
                                  value={actionItemsSearch}
                                  onChange={(e) => setActionItemsSearch(e.target.value)}
                                  className="w-full"
                                  disabled={dataSource !== 'api'}
                                />
                              </div>
                            </div>
                            
                            {/* Audio-based filtering indicator */}
                            {currentAudioTime > 0 && (
                              <div className="flex items-center gap-2 text-sm bg-blue-50 p-2 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2">
                                  <Badge variant="default" className="bg-blue-100 text-blue-800">
                                    {isAudioPlaying ? '' : 'PAUSED |'} Audio Filter: {Math.floor(Math.floor(currentAudioTime / 300) * 300 / 60)}:00 - {Math.floor((Math.floor(currentAudioTime / 300) * 300 + 300) / 60)}:00
                                  </Badge>
                                  <span className="text-blue-600">({filteredActionItems?.length || 0} items)</span>
                                </div>
                              </div>
                            )}
                            
                            {actionItemsSearch && currentAudioTime === 0 && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>Showing {filteredActionItems?.length || 0} of {dataAPI?.intelligence?.actions?.length || 0} action items</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setActionItemsSearch('')}
                                >
                                  Clear search
                                </Button>
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Action Items</h3>
                                {
                                  dataSource === 'api' ? (
                                    <Badge variant="outline">{filteredActionItems?.length || 0}</Badge>
                                  ) : (
                                    <SkeletonBar width="30px" height="12px" />
                                  )
                                }
                                {(filteredActionItems?.length || 0) !== (dataAPI?.intelligence?.actions?.length || 0) && (
                                  <Badge variant="secondary" className="text-xs">
                                    of {dataAPI?.intelligence?.actions?.length || 0} total
                                  </Badge>
                                )}
                              </div>
                              {(filteredActionItems?.length || 0) > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {dataSource !== 'api' ? (
                                    // Show skeleton loading for sample data
                                    Array.from({ length: 5 }).map((_, index) => (
                                      <IntelligenceItemSkeleton key={index} />
                                    ))
                                  ) : (
                                    filteredActionItems.map((item: {task?: string; assigned_to?:string; timestamp_start?: string | null; deadline?: string; timestamp_end?: string; confidence?: string}, index: number) => {
                                      const isCurrentlyActive = currentActiveItems.actionItems.some(activeItem => 
                                        activeItem.timestamp_start === item.timestamp_start && 
                                        activeItem.timestamp_end === item.timestamp_end
                                      );
                                      return (
                                        <Card key={index} className={`border-l-4 ${
                                          isCurrentlyActive 
                                            ? 'border-l-blue-500 bg-blue-50 shadow-md' 
                                          : 'border-l-blue-500'
                                      }`}>
                                        <CardContent className="p-4">
                                          <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center text-xs gap-2">
                                              Confidence:
                                              <Badge variant={item.confidence ? getSeverityVariant(item.confidence) : 'default'} className="text-xs">
                                                {item.confidence}
                                              </Badge>
                                              {isCurrentlyActive && (
                                                <Badge variant="default" className="text-xs bg-blue-100 text-blue-800">
                                                  Active
                                                </Badge>
                                              )}
                                            </div>
                                            <div className={`text-xs ${
                                              isCurrentlyActive ? 'text-blue-600 font-bold' : 'text-gray-500'
                                            }`}>
                                              {/* {formatTimestamp(item.timestamp_start)} - {formatTimestamp(item.timestamp_end)} */}
                                              {item.timestamp_start && formatTimestampFromString(item.timestamp_start)}
                                            </div>
                                          </div>
                                          <p className={`text-sm mb-2 ${
                                            isCurrentlyActive ? 'text-blue-900 font-medium' : 'text-gray-900'
                                          }`}>
                                            {actionItemsSearch && item.task ? (
                                              item.task.split(new RegExp(`(${actionItemsSearch})`, 'gi')).map((part: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined, i: React.Key | null | undefined) =>
                                                typeof part === 'string' && part.toLowerCase() === actionItemsSearch.toLowerCase() ? (
                                                  <mark key={i} className="bg-yellow-200 px-1 rounded">
                                                    {part}
                                                  </mark>
                                                ) : (
                                                  part
                                                )
                                              )
                                            ) : (
                                              item.task
                                            )}
                                          </p>
                                          {item.assigned_to && (
                                            <p className={`text-sm ${
                                              isCurrentlyActive ? 'text-blue-700' : 'text-gray-600'
                                            }`}>
                                              Assigned To: {actionItemsSearch && item.assigned_to.toLowerCase().includes(actionItemsSearch.toLowerCase()) ? (
                                                item.assigned_to.split(new RegExp(`(${actionItemsSearch})`, 'gi')).map((part: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined, i: React.Key | null | undefined) =>
                                                  typeof part === 'string' && part.toLowerCase() === actionItemsSearch.toLowerCase() ? (
                                                    <mark key={i} className="bg-yellow-200 px-1 rounded">
                                                      {part}
                                                    </mark>
                                                  ) : (
                                                    part
                                                  )
                                                )
                                              ) : (
                                                item.assigned_to
                                              )}
                                            </p>
                                          )}
                                          {item.deadline && (
                                            <p className={`text-sm ${
                                              isCurrentlyActive ? 'text-blue-700' : 'text-gray-600'
                                            }`}>Due: {item.deadline && new Date(item.deadline).toLocaleDateString()}</p>
                                          )}
                                        </CardContent>
                                      </Card>
                                    );
                                  })
                                )}
                                </div>
                              ) : (
                                <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                                  {actionItemsSearch 
                                    ? 'No action items match your search criteria' 
                                    : 'No action items identified'
                                  }
                                </div>
                              )}
                            </div>

                            {/* Free Trial Upgrade Banner for Action Items */}
                            {isFreeTrial && hasExceededFreeTrialLimit && (
                              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="pr-4">
                                    <h4 className="text-xs font-medium text-blue-800">More Action Items Available</h4>
                                    <p className="text-xs text-blue-700">
                                      Additional action items exist beyond the 5-minute free trial limit.
                                    </p>
                                  </div>
                                  <Button
                                    onClick={handleSubscribe}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                    size="sm"
                                  >
                                    Subscribe to a plan
                                  </Button>
                                </div>
                              </div>
                            )}
                          </TabsContent>

                          {/* Decisions Tab */}
                          <TabsContent value="decisions" className="space-y-4">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Search decisions..."
                                  value={decisionsSearch}
                                  onChange={(e) => setDecisionsSearch(e.target.value)}
                                  className="w-full"
                                  disabled={dataSource !== 'api'}
                                />
                              </div>
                            </div>
                            
                            {/* Audio-based filtering indicator */}
                            {currentAudioTime > 0 && (
                              <div className="flex items-center gap-2 text-sm bg-green-50 p-2 rounded-lg border border-green-200">
                                <div className="flex items-center gap-2">
                                  <Badge variant="default" className="bg-green-100 text-green-800">
                                    {isAudioPlaying ? '' : 'PAUSED |'} Audio Filter: {Math.floor(Math.floor(currentAudioTime / 300) * 300 / 60)}:00 - {Math.floor((Math.floor(currentAudioTime / 300) * 300 + 300) / 60)}:00
                                  </Badge>
                                  <span className="text-green-600">({filteredDecisions?.length || 0} items)</span>
                                </div>
                              </div>
                            )}
                            
                            {decisionsSearch && currentAudioTime === 0 && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>Showing {filteredDecisions?.length || 0} of {dataAPI?.intelligence?.decisions?.length || 0} decisions</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setDecisionsSearch('')}
                                >
                                  Clear search
                                </Button>
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Decisions</h3>
                                {
                                  dataSource === 'api' ? (
                                    <Badge variant="outline">{filteredDecisions?.length || 0}</Badge>
                                  ) : (
                                    <SkeletonBar width="30px" height="12px" />
                                  )
                                }
                                {(filteredDecisions?.length || 0) !== (dataAPI?.intelligence?.decisions?.length || 0) && (
                                  <Badge variant="secondary" className="text-xs">
                                    of {dataAPI?.intelligence?.decisions.length || 0} total
                                  </Badge>
                                )}
                              </div>
                              {(filteredDecisions?.length || 0) > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {dataSource === 'sample' ? (
                                    // Show skeleton loading for sample data
                                    Array.from({ length: 4 }).map((_, index) => (
                                      <IntelligenceItemSkeleton key={index} />
                                    ))
                                  ) : (
                                    filteredDecisions.map((decision: {decision?: string; confidence?: string; reason?: string; timestamp_start?: string | null; timestamp_end?: string; assigned_to?: string}, index: number) => {
                                      const isCurrentlyActive = currentActiveItems.decisions.some(activeDecision => 
                                        activeDecision.timestamp_start === decision.timestamp_start && 
                                        activeDecision.timestamp_end === decision.timestamp_end
                                      );
                                      return (
                                        <Card key={index} className={`border-l-4 ${
                                          isCurrentlyActive 
                                            ? 'border-l-green-500 bg-green-50 shadow-md' 
                                            : 'border-l-green-500'
                                        }`}>
                                          <CardContent className="p-4">
                                            <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                              <Badge variant={decision?.confidence ? getSeverityVariant(decision?.confidence) : 'default'} className="text-xs">
                                                {decision.confidence}
                                              </Badge>
                                              {isCurrentlyActive && (
                                                <Badge variant="default" className="text-xs bg-green-100 text-green-800">
                                                  Active
                                                </Badge>
                                              )}
                                            </div>
                                            <div className={`text-xs ${
                                              isCurrentlyActive ? 'text-green-600 font-bold' : 'text-gray-500'
                                            }`}>
                                              {/* {formatTimestamp(decision.timestamp_start)} - {formatTimestamp(decision.timestamp_end)} */}
                                              {decision.timestamp_start && formatTimestampFromString(decision.timestamp_start)}
                                            </div>
                                          </div>
                                          <p className={`text-sm mb-2 ${
                                            isCurrentlyActive ? 'text-green-900 font-medium' : 'text-gray-900'
                                          }`}>
                                            {decisionsSearch && decision.decision ? (
                                              decision.decision.split(new RegExp(`(${decisionsSearch})`, 'gi')).map((part: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined, i: React.Key | null | undefined) =>
                                                typeof part === 'string' && part.toLowerCase() === decisionsSearch.toLowerCase() ? (
                                                  <mark key={i} className="bg-yellow-200 px-1 rounded">
                                                    {part}
                                                  </mark>
                                                ) : (
                                                  part
                                                )
                                              )
                                            ) : (
                                              decision.decision
                                            )}
                                          </p>
                                          {decision.reason && (
                                            <div className={`text-sm p-2 rounded ${
                                              isCurrentlyActive 
                                                ? 'text-green-700 bg-green-100' 
                                                : 'text-gray-600 bg-green-50'
                                            }`}>
                                              <strong>Reason:</strong> {decision.reason}
                                            </div>
                                          )}
                                        </CardContent>
                                      </Card>
                                    );
                                  })
                                )}
                                </div>
                              ) : (
                                <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                                  {decisionsSearch 
                                    ? 'No decisions match your search criteria' 
                                    : 'No decisions identified'
                                  }
                                </div>
                              )}
                            </div>

                            {/* Free Trial Upgrade Banner for Decisions */}
                            {isFreeTrial && hasExceededFreeTrialLimit && (
                              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="pr-4">
                                    <h4 className="text-xs font-medium text-blue-800">More Decisions Available</h4>
                                    <p className="text-xs text-blue-700">
                                      Additional decisions exist beyond the 5-minute free trial limit.
                                    </p>
                                  </div>
                                  <Button
                                    onClick={handleSubscribe}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                    size="sm"
                                  >
                                    Subscribe to a plan
                                  </Button>
                                </div>
                              </div>
                            )}
                          </TabsContent>

                          {/* Issues Tab */}
                          <TabsContent value="issues" className="space-y-4">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Search issues..."
                                  value={issuesSearch}
                                  onChange={(e) => setIssuesSearch(e.target.value)}
                                  className="w-full"
                                  disabled={dataSource !== 'api'}
                                />
                              </div>
                            </div>
                            
                            {/* Audio-based filtering indicator */}
                            {currentAudioTime > 0 && (
                              <div className="flex items-center gap-2 text-sm bg-red-50 p-2 rounded-lg border border-red-200">
                                <div className="flex items-center gap-2">
                                  <Badge variant="default" className="bg-red-100 text-red-800">
                                    {isAudioPlaying ? '' : 'PAUSED |'} Audio Filter: {Math.floor(Math.floor(currentAudioTime / 300) * 300 / 60)}:00 - {Math.floor((Math.floor(currentAudioTime / 300) * 300 + 300) / 60)}:00
                                  </Badge>
                                  <span className="text-red-600">({filteredIssues?.length || 0} items)</span>
                                </div>
                              </div>
                            )}
                            
                            {issuesSearch && currentAudioTime === 0 && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>Showing {filteredIssues?.length || 0} of {dataAPI?.intelligence?.issues?.length || 0} issues</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setIssuesSearch('')}
                                >
                                  Clear search
                                </Button>
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Issues</h3>
                                {
                                  dataSource === 'api' ? (
                                    <Badge variant="outline">{filteredIssues?.length || 0}</Badge>
                                  ) : (
                                    <SkeletonBar width="30px" height="12px" />
                                  )
                                }
                                {(filteredIssues?.length || 0) !== (dataAPI?.intelligence?.issues?.length || 0) && (
                                  <Badge variant="secondary" className="text-xs">
                                    of {dataAPI?.intelligence?.issues.length || 0} total
                                  </Badge>
                                )}
                              </div>
                              {(filteredIssues?.length || 0) > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {dataSource === 'sample' ? (
                                    // Show skeleton loading for sample data
                                    Array.from({ length: 3 }).map((_, index) => (
                                      <IntelligenceItemSkeleton key={index} />
                                    ))
                                  ) : (
                                    filteredIssues.map((issue: { issue: string; timestamp_start: string; timestamp_end: string }, index: number) => {
                                      const isCurrentlyActive = currentActiveItems.issues.some(activeIssue => 
                                        activeIssue.timestamp_start === issue.timestamp_start && 
                                        activeIssue.timestamp_end === issue.timestamp_end
                                      );
                                      return (
                                        <Card key={index} className={`border-l-4 ${
                                          isCurrentlyActive 
                                            ? 'border-l-red-500 bg-red-50 shadow-md' 
                                            : 'border-l-red-500'
                                        }`}>
                                          <CardContent className="p-4">
                                            <div className="flex items-start justify-between mb-2">
                                            {isCurrentlyActive && (
                                              <Badge variant="default" className="text-xs bg-red-100 text-red-800">
                                                Active
                                              </Badge>
                                            )}
                                            <div className={`text-xs ${
                                              isCurrentlyActive ? 'text-red-600 font-bold' : 'text-gray-500'
                                            }`}>
                                              {/* {formatTimestamp(issue.timestamp_start)} - {formatTimestamp(issue.timestamp_end)} */}
                                              {formatTimestampFromString(issue.timestamp_start)}
                                            </div>
                                          </div>
                                          <p className={`text-sm mb-2 ${
                                            isCurrentlyActive ? 'text-red-900 font-medium' : 'text-gray-900'
                                          }`}>
                                            {issuesSearch && issue.issue ? (
                                              issue.issue.split(new RegExp(`(${issuesSearch})`, 'gi')).map((part: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined, i: React.Key | null | undefined) =>
                                                typeof part === 'string' && part.toLowerCase() === issuesSearch.toLowerCase() ? (
                                                  <mark key={i} className="bg-yellow-200 px-1 rounded">
                                                    {part}
                                                  </mark>
                                                ) : (
                                                  part
                                                )
                                              )
                                            ) : (
                                              issue.issue
                                            )}
                                          </p>
                                        </CardContent>
                                      </Card>
                                    );
                                  })
                                )}
                                </div>
                              ) : (
                                <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                                  {issuesSearch 
                                    ? 'No issues match your search criteria' 
                                    : 'No issues identified'
                                  }
                                </div>
                              )}
                            </div>

                            {/* Free Trial Upgrade Banner for Issues */}
                            {isFreeTrial && hasExceededFreeTrialLimit && (
                              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="pr-4">
                                    <h4 className="text-xs font-medium text-blue-800">More Issues Available</h4>
                                    <p className="text-xs text-blue-700">
                                      Additional issues exist beyond the 5-minute free trial limit.
                                    </p>
                                  </div>
                                  <Button
                                    onClick={handleSubscribe}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                    size="sm"
                                  >
                                    Subscribe to a plan
                                  </Button>
                                </div>
                              </div>
                            )}
                          </TabsContent>

                          {/* Questions Tab */}
                          <TabsContent value="questions" className="space-y-4">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Input
                                  placeholder="Search questions..."
                                  value={questionsSearch}
                                  onChange={(e) => setQuestionsSearch(e.target.value)}
                                  className="w-full"
                                  disabled={dataSource !== 'api'}
                                />
                              </div>
                            </div>
                            
                            {/* Audio-based filtering indicator */}
                            {currentAudioTime > 0 && (
                              <div className="flex items-center gap-2 text-sm bg-yellow-50 p-2 rounded-lg border border-yellow-200">
                                <div className="flex items-center gap-2">
                                  <Badge variant="default" className="bg-yellow-100 text-yellow-800">
                                    {isAudioPlaying ? '' : 'PAUSED |'} Audio Filter: {Math.floor(Math.floor(currentAudioTime / 300) * 300 / 60)}:00 - {Math.floor((Math.floor(currentAudioTime / 300) * 300 + 300) / 60)}:00
                                  </Badge>
                                  <span className="text-yellow-600">({filteredQuestions?.length || 0} items)</span>
                                </div>
                              </div>
                            )}
                            
                            {questionsSearch && currentAudioTime === 0 && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>Showing {filteredQuestions?.length || 0} of {dataAPI?.intelligence?.questions?.length || 0} questions</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setQuestionsSearch('')}
                                >
                                  Clear search
                                </Button>
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <div className="flex items-center gap-3 mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Questions</h3>
                                {
                                  dataSource === 'api' ? (
                                    <Badge variant="outline">{filteredQuestions?.length || 0}</Badge>
                                  ) : (
                                    <SkeletonBar width="30px" height="12px" />
                                  )
                                }
                                {(filteredQuestions?.length || 0) !== (dataAPI?.intelligence?.questions?.length || 0) && (
                                  <Badge variant="secondary" className="text-xs">
                                    of {dataAPI?.intelligence?.questions.length || 0} total
                                  </Badge>
                                )}
                              </div>
                              {(filteredQuestions?.length || 0) > 0 ? (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {dataSource === 'sample' ? (
                                    // Show skeleton loading for sample data
                                    Array.from({ length: 3 }).map((_, index) => (
                                      <IntelligenceItemSkeleton key={index} />
                                    ))
                                  ) : (
                                    filteredQuestions.map((question: { question: string; timestamp_start: string; timestamp_end: string }, index: number) => {
                                      const isCurrentlyActive = currentActiveItems.questions.some(activeQuestion => 
                                        activeQuestion.timestamp_start === question.timestamp_start && 
                                        activeQuestion.timestamp_end === question.timestamp_end
                                      );
                                      return (
                                        <Card key={index} className={`border-l-4 ${
                                          isCurrentlyActive 
                                            ? 'border-l-yellow-500 bg-yellow-50 shadow-md' 
                                            : 'border-l-yellow-500'
                                        }`}>
                                          <CardContent className="p-4">
                                          <div className="flex items-start justify-between mb-2">
                                            {isCurrentlyActive && (
                                              <Badge variant="default" className="text-xs bg-yellow-100 text-yellow-800">
                                                Active
                                              </Badge>
                                            )}
                                            <div className={`text-xs ${
                                              isCurrentlyActive ? 'text-yellow-600 font-bold' : 'text-gray-500'
                                            }`}>
                                              {/* {formatTimestamp(question.timestamp_start)} - {formatTimestamp(question.timestamp_end)} */}
                                              {formatTimestampFromString(question.timestamp_start)}
                                            </div>
                                          </div>
                                          <p className={`text-sm mb-2 ${
                                            isCurrentlyActive ? 'text-yellow-900 font-medium' : 'text-gray-900'
                                          }`}>
                                            {questionsSearch && question.question ? (
                                              question.question.split(new RegExp(`(${questionsSearch})`, 'gi')).map((part: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined, i: React.Key | null | undefined) =>
                                                typeof part === 'string' && part.toLowerCase() === questionsSearch.toLowerCase() ? (
                                                  <mark key={i} className="bg-yellow-200 px-1 rounded">
                                                    {part}
                                                  </mark>
                                                ) : (
                                                  part
                                                )
                                              )
                                            ) : (
                                              question.question
                                            )}
                                          </p>
                                        </CardContent>
                                      </Card>
                                    );
                                  })
                                )}
                                </div>
                              ) : (
                                <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                                  {questionsSearch 
                                    ? 'No questions match your search criteria' 
                                    : 'No questions identified'
                                  }
                                </div>
                              )}
                            </div>

                            {/* Free Trial Upgrade Banner for Questions */}
                            {isFreeTrial && hasExceededFreeTrialLimit && (
                              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="pr-4">
                                    <h4 className="text-xs font-medium text-blue-800">More Questions Available</h4>
                                    <p className="text-xs text-blue-700">
                                      Additional questions exist beyond the 5-minute free trial limit.
                                    </p>
                                  </div>
                                  <Button
                                    onClick={handleSubscribe}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                    size="sm"
                                  >
                                    Subscribe to a plan
                                  </Button>
                                </div>
                              </div>
                            )}
                          </TabsContent>
                        </Tabs>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
                <div className="pl-3">
                  {/* Audio Soundwave Visualization */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700">Audio Visualization</h3>
                      <Badge variant={isAudioPlaying ? "default" : "secondary"} className="text-xs">
                        {isAudioPlaying ? "Playing" : "Paused"}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-center space-x-1 h-20 bg-white/50 rounded-lg p-3">
                      {Array.from({ length: 24 }).map((_, index) => {
                        const height = isAudioPlaying && frequencyData[index] 
                          ? Math.max((frequencyData[index] / 255) * 100, 5)
                          : Math.random() * 20 + 5; // Subtle random movement when paused
                        
                        return (
                          <div
                            key={index}
                            className="flex-1 max-w-[4px] bg-gradient-to-t from-blue-400 to-blue-600 rounded-full transition-all duration-100 ease-out"
                            style={{
                              height: `${height}%`,
                              opacity: isAudioPlaying ? 0.8 : 0.3,
                              transform: `scaleY(${isAudioPlaying ? 1 : 0.3})`,
                            }}
                          />
                        );
                      })}
                    </div>
                    
                    {/* <div className="mt-2 text-center">
                      <span className="text-xs text-gray-500">
                        {isAudioPlaying ? "Real-time audio analysis" : "Click play to see audio visualization"}
                      </span>
                    </div> */}
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 mb-6 p-2 rounded-lg border border-blue-100">
                    <div className="mb-4">
                      {/* <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {sampleTranscriptData.file_name}
                      </h4> */}
                      <audio 
                        ref={audioRef}
                        controls 
                        className="w-full h-12"
                        preload="metadata"
                        style={{
                          filter: 'sepia(20%) saturate(70%) hue-rotate(200deg) brightness(1.1)',
                        }}
                      >
                        {audioUrl && <source src={audioUrl} type="audio/mpeg" />}
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                          MP3 Audio
                        </Badge>
                        {isAudioPlaying && (
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            Playing: {Math.floor(currentAudioTime / 60)}:{String(Math.floor(currentAudioTime % 60)).padStart(2, '0')}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <span className="text-xs">🎧 High Quality</span>
                      </div>
                    </div>
                  </div>
                  <div className="mb-6">
                    <CardTitle className="text-lg mb-4">Executive Summary</CardTitle>
                    {dataSource === 'api' ? (
                      <p className="text-gray-700 mb-4">
                        {dataAPI?.intelligence?.summary || 'No summary available.'}
                      </p>
                    ) : (
                      <TextSkeleton lines={3} />
                    )}
                  </div>
                  <div className="mb-6">
                    <CardTitle className="text-lg mb-4">Key Topics</CardTitle>
                    {dataSource === 'api' ? (
                      <div className="flex flex-wrap gap-2">
                        {(dataAPI?.intelligence?.topics || []).map((topic: string, index: number) => (
                          <Badge key={index} variant="secondary">{topic}</Badge>
                        ))}
                      </div>
                    ) : (
                      <BadgesSkeleton count={4} />
                    )}
                  </div>
                  <div className="mb-6">
                    <CardTitle className="text-lg mb-2">Notes</CardTitle>
                    {dataSource === 'api' ? (
                      <p className="text-amber-700 bg-amber-50 p-3 text-xs rounded-lg">
                        {dataAPI?.intelligence?.notes || 'No additional notes available.'}
                      </p>
                    ) : (
                      <div className="bg-amber-50 p-3 rounded-lg">
                        <TextSkeleton lines={2} />
                      </div>
                    )}
                  </div>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <CardTitle className="text-lg">Exports</CardTitle>
                      {isFreeTrial && (
                        <Button
                          onClick={handleSubscribe}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          size="sm"
                        >
                          Subscribe to plan
                        </Button>
                      )}
                    </div>
                    
                    {/* Email Success/Error Messages */}
                    {emailSuccess && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-700 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {emailSuccess}
                        </div>
                      </div>
                    )}
                    {emailError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-red-700 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          {emailError}
                        </div>
                      </div>
                    )}

                    {activeAutomationTab === 'transcript' ? (
                      // Transcript Export Options
                      <div className="space-y-4">
                        <div className="text-sm font-medium text-gray-700 mb-3">Transcript Content:</div>
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Full Text and Segments Option */}
                          <div className="border rounded-lg p-3 bg-gradient-to-r from-blue-100 to-indigo-50 hover:bg-gray-50 transition-colors md:col-span-2">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="font-medium text-sm">Full Text + Segments</div>
                                <div className="text-xs text-gray-600">Complete transcript with timestamped segments</div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {
                                fullSegmentButtons.map((format) => (
                                  <Button 
                                    key={format}
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    disabled={
                                      dataSource === 'api' ? isFreeTrial : true
                                    }
                                    onClick={() => exportTranscriptData('full-segments', format.toLowerCase() as 'pdf' | 'json' | 'csv' | 'txt')}
                                  >
                                    {format}
                                  </Button>
                                ))
                              }
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                // disabled={
                                //   (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                // }
                                onClick={() => {
                                  const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                  if (email) {
                                    emailTranscriptData('full-segments', email);
                                  }
                                }}
                              >
                                {isEmailingTranscript ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Email
                                  </span>
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Other Exports Accordion for Transcript */}
                          <div className="md:col-span-2">
                            <div 
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors border"
                              onClick={() => setIsOtherExportsOpen(!isOtherExportsOpen)}
                            >
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-sm text-gray-700">Other Exports</h4>
                                <Badge variant="outline" className="text-xs">2 types</Badge>
                              </div>
                              <div className="text-gray-500 transition-transform duration-200" style={{
                                transform: isOtherExportsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                              }}>
                              </div>
                            </div>
                            
                            <div className={`grid gap-4 mt-2 transition-all duration-300 ease-in-out overflow-hidden ${
                              isOtherExportsOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                            }`}>

                              {/* Full Text Only Option */}
                              <div className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <div className="font-medium text-sm">Full Text Only</div>
                                    <div className="text-xs text-gray-600">Complete transcript as continuous text</div>
                                  </div>
                                </div>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  {
                                    fullOnlyButtons.map((format) => (
                                      <Button 
                                        key={format}
                                        size="sm" 
                                        variant="outline" 
                                        className="text-xs"
                                        disabled={dataSource === 'api' ? isFreeTrial : true}
                                        onClick={() => exportTranscriptData('full-only', format.toLowerCase() as 'pdf' | 'json' | 'txt')}
                                      >
                                        {format}
                                      </Button>
                                    ))
                                  }
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                    disabled={
                                      (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                    }
                                    onClick={() => {
                                      const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                      if (email) {
                                        emailTranscriptData('full-only', email);
                                      }
                                    }}
                                  >
                                    {isEmailingTranscript ? (
                                      <span className="flex items-center gap-1">
                                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Sending...
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        Email
                                      </span>
                                    )}
                                  </Button>
                                </div>
                              </div>

                              {/* Segments Only Option */}
                              <div className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <div className="font-medium text-sm">Segments Only</div>
                                    <div className="text-xs text-gray-600">Timestamped segments with speakers</div>
                                  </div>
                                </div>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  {
                                    segmentsOnlyButtons.map((format) => (
                                      <Button 
                                        key={format}
                                        size="sm" 
                                        variant="outline" 
                                        className="text-xs"
                                        disabled={dataSource === 'api' ? isFreeTrial : true}
                                        onClick={() => exportTranscriptData('segments-only', format.toLowerCase() as 'pdf' | 'json' | 'txt' | 'csv')}
                                      >
                                        {format}
                                      </Button>
                                    ))
                                  }
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                    disabled={
                                      (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                    }
                                    onClick={() => {
                                      const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                      if (email) {
                                        emailTranscriptData('segments-only', email);
                                      }
                                    }}
                                  >
                                    {isEmailingTranscript ? (
                                      <span className="flex items-center gap-1">
                                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Sending...
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        Email
                                      </span>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Intelligence Analysis Export Options
                      <div className="space-y-4">
                        <div className="text-sm font-medium text-gray-700 mb-3">Intelligence Analysis:</div>
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* All Intelligence Data */}
                          <div className="border rounded-lg p-3 bg-gradient-to-r from-blue-100 to-indigo-50 hover:bg-gray-50 transition-colors md:col-span-2">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="font-medium text-sm">Complete Analysis</div>
                                <div className="text-xs text-gray-600">All action items, decisions, issues & questions</div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {
                                completeAnalysisButtons.map((format) => (
                                  <Button 
                                    key={format}
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    disabled={dataSource === 'api' ? isFreeTrial : true}
                                    onClick={() => exportIntelligenceData('all', format.toLowerCase() as 'pdf' | 'json' | 'txt' | 'csv')}
                                  >
                                    {format}
                                  </Button>
                                ))
                              }
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                disabled={
                                  (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                }
                                onClick={() => {
                                  const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                  if (email) {
                                    emailIntelligenceData('all', email);
                                  }
                                }}
                              >
                                {isEmailingTranscript ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Email
                                  </span>
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Other Exports Accordion */}
                          <div className="md:col-span-2">
                            <div 
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors border"
                              onClick={() => setIsOtherExportsOpen(!isOtherExportsOpen)}
                            >
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-sm text-gray-700">Other Exports</h4>
                                <Badge variant="outline" className="text-xs">4 types</Badge>
                              </div>
                              <div className="text-gray-500 transition-transform duration-200" style={{
                                transform: isOtherExportsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                              }}>
                              </div>
                            </div>
                            
                            <div className={`grid gap-4 mt-2 transition-all duration-300 ease-in-out overflow-hidden ${
                              isOtherExportsOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                            }`}>

                          {/* Action Items Only */}
                          <div className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="font-medium text-sm">Action Items</div>
                                <div className="text-xs text-gray-600">Tasks and assignments identified</div>
                              </div>
                              {
                                dataSource === 'api' ? (
                                  <Badge variant="outline" className="text-xs">
                                    {detailedIntelligence?.action_items?.length || 0}
                                  </Badge>
                                ) : (
                                  <SkeletonBar width="30px" height="12px" />
                                )
                              }
                            </div>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {
                                actionItemsButtons.map((format) => (
                                  <Button 
                                    key={format}
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    disabled={dataSource === 'api' ? isFreeTrial : true}
                                    onClick={() => exportIntelligenceData('action-items', format.toLowerCase() as 'pdf' | 'json' | 'txt' | 'csv')}
                                  >
                                    {format}
                                  </Button>
                                ))
                              }
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                disabled={
                                  (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                }
                                onClick={() => {
                                  const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                  if (email) {
                                    emailIntelligenceData('action-items', email);
                                  }
                                }}
                              >
                                {isEmailingTranscript ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Email
                                  </span>
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Decisions Only */}
                          <div className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="font-medium text-sm">Decisions</div>
                                <div className="text-xs text-gray-600">Key decisions made during discussion</div>
                              </div>
                              {
                                dataSource === 'api' ? (
                                  <Badge variant="outline" className="text-xs">
                                    {detailedIntelligence?.decisions?.length || 0}
                                  </Badge>
                                ) : (
                                  <SkeletonBar width="30px" height="12px" />
                                )
                              }
                            </div>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {
                                decisionsButtons.map((format) => (
                                  <Button 
                                    key={format}
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    disabled={dataSource === 'api' ? isFreeTrial : true}
                                    onClick={() => exportIntelligenceData('decisions', format.toLowerCase() as 'pdf' | 'json' | 'txt' | 'csv')}
                                  >
                                    {format}
                                  </Button>
                                ))
                              }
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                disabled={
                                  (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                }
                                onClick={() => {
                                  const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                  if (email) {
                                    emailIntelligenceData('decisions', email);
                                  }
                                }}
                              >
                                {isEmailingTranscript ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Email
                                  </span>
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Issues Only */}
                          <div className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="font-medium text-sm">Issues</div>
                                <div className="text-xs text-gray-600">Problems and concerns raised</div>
                              </div>
                              {
                                dataSource === 'api' ? (
                                  <Badge variant="outline" className="text-xs">
                                    {detailedIntelligence?.issues?.length || 0}
                                  </Badge>
                                ) : (
                                  <SkeletonBar width="30px" height="12px" />
                                )
                              }
                            </div>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {
                                issuesButtons.map((format) => (
                                  <Button 
                                    key={format}
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    disabled={dataSource === 'api' ? isFreeTrial : true}
                                    onClick={() => exportIntelligenceData('issues', format.toLowerCase() as 'pdf' | 'json' | 'txt' | 'csv')}
                                  >
                                    {format}
                                  </Button>
                                ))
                              }
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                disabled={
                                  (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                }
                                onClick={() => {
                                  const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                  if (email) {
                                    emailIntelligenceData('issues', email);
                                  }
                                }}
                              >
                                {isEmailingTranscript ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Email
                                  </span>
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Questions Only */}
                          <div className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="font-medium text-sm">Questions</div>
                                <div className="text-xs text-gray-600">Unresolved questions and inquiries</div>
                              </div>
                              {
                                dataSource === 'api' ? (
                                  <Badge variant="outline" className="text-xs">
                                    {detailedIntelligence?.questions?.length || 0}
                                  </Badge>
                                ) : (
                                  <SkeletonBar width="30px" height="12px" />
                                )
                              }
                            </div>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {
                                questionsButtons.map((format) => (  
                                  <Button 
                                    key={format}
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    disabled={dataSource === 'api' ? isFreeTrial : true}
                                    onClick={() => exportIntelligenceData('questions', format.toLowerCase() as 'pdf' | 'json' | 'txt' | 'csv')}
                                  >
                                    {format}
                                  </Button>
                                ))
                              }
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                                disabled={
                                  (dataSource === 'api' ? isFreeTrial : true) || isEmailingTranscript || !clerkUser?.primaryEmailAddress?.emailAddress
                                }
                                onClick={() => {
                                  const email = clerkUser?.primaryEmailAddress?.emailAddress;
                                  if (email) {
                                    emailIntelligenceData('questions', email);
                                  }
                                }}
                              >
                                {isEmailingTranscript ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Sending...
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Email
                                  </span>
                                )}
                              </Button>
                            </div>
                          </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RecordingDetail;
